import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button, Tag, Divider } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { mockBlogPosts } from '../models';
import { BlogNavbar } from '../components/website/BlogNavbar';
import './BlogDetail.css';

export function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  
  const post = mockBlogPosts.find(p => p.slug === slug);
  
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Simple markdown parser for basic formatting
  const parseMarkdown = (content: string) => {
    return content
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/^(?!<[h|u|l])/gm, '<p>')
      .replace(/(?<!>)$/gm, '</p>')
      .replace(/<p><\/p>/g, '')
      .replace(/<p>(<h[1-6]>)/g, '$1')
      .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
      .replace(/<p>(<ul>)/g, '$1')
      .replace(/(<\/ul>)<\/p>/g, '$1');
  };

  return (
    <div className="blog-detail-page">
      <BlogNavbar />
      <div className="container plr-page">
        <div className="blog-detail-header">
          <Link to="/blog" className="back-link">
            <Button icon={<ArrowLeftOutlined />} size="large" type="text">
              Back to Blog
            </Button>
          </Link>
        </div>

        <article className="blog-article">
          <header className="article-header">
            <h1 className="article-title">{post.title}</h1>
            
            <div className="article-meta">
              <div className="meta-item">
                <UserOutlined /> {post.author}
              </div>
              <div className="meta-item">
                <CalendarOutlined /> {formatDate(post.publishedDate)}
              </div>
              <div className="meta-item">
                <ClockCircleOutlined /> {post.readTime} min read
              </div>
            </div>

            <div className="article-tags">
              {post.tags.map(tag => (
                <Tag key={tag} color="blue" className="article-tag">
                  {tag}
                </Tag>
              ))}
            </div>

            {post.featuredImage && (
              <div className="article-image">
                <img src={post.featuredImage} alt={post.title} />
              </div>
            )}
          </header>

          <Divider />

          <div className="article-content">
            <div dangerouslySetInnerHTML={{ __html: parseMarkdown(post.content) }} />
          </div>
        </article>

        <div className="blog-detail-footer">
          <Divider />
          <div className="footer-actions">
            <Link to="/blog">
              <Button size="large" type="primary">
                Read More Articles
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}